import { flatten, get, partial, snakeCase, values } from 'lodash';

import { serviceUtils } from '@zapier/common-utils';

import ServiceUtils from 'app/service/ServiceUtils';

export const sortAuthsByAppAndTitle = authsToBeSorted => {
  const authsByApp = authsToBeSorted.reduce(
    (byApp, auth) => ({
      ...byApp,
      [auth.api]: [...(byApp[auth.api] || []), auth],
    }),
    {}
  );

  return flatten(
    Object.keys(authsByApp)
      .sort()
      .map(api =>
        authsByApp[api].sort((auth1, auth2) =>
          auth1.title < auth2.title ? -1 : 1
        )
      )
  );
};

export const toAuthProperty = property => snakeCase(property);

// Take a single connectKey from the query string and break it up into its
// parts. There are three possible formats:
// api_key
//   For example: TrelloAPI
// source_auth_id:destination_api_key
//   For example: 8:TrelloAPI
// source_api_key:source_auth_identity:destination_api_key
//   For example: GoogleMailV2API:joe.foo@zapier.com:SlackAPI
export const splitConnectKey = connectKey => {
  const parts = connectKey.split(':');
  if (parts.length > 3) {
    return null;
  }
  const nonBlankParts = parts.filter(Boolean);
  if (nonBlankParts.length < parts.length) {
    return null;
  }
  if (parts.length === 1) {
    return {
      sourceApi: parts[0],
    };
  } else if (parts.length === 2) {
    const sourceAuthId = parseInt(parts[0]);
    if (!isNaN(sourceAuthId)) {
      return {
        sourceAuthId,
        // This is how the backend treats destination_selected_api
        // https://zapier.slack.com/archives/CHRQEPZGR/p1559597450044800
        destinationApi: serviceUtils.stripVersion(parts[1]),
      };
    }
  } else if (parts.length === 3) {
    return {
      sourceApi: parts[0],
      sourceAuthIdentifier: parts[1],
      // See comment above about destination_selected_api
      destinationApi: serviceUtils.stripVersion(parts[2]),
    };
  }
  return null;
};

// Split a `connect` parameter from the query string into multiple connection
// info objects. For example, a string like:
//   TrelloAPI,8:TrelloAPI,GoogleMailV2API:joe.foo@zapier.com:SlackAPI
// Becomes three objects representing three potential auth connections.
const getUniqueConnectKeys = connectKeysString => {
  return [
    ...new Set(
      (connectKeysString || '')
        .split(',')
        .map(key => key.trim())
        .map(key => splitConnectKey(key) && key)
        .filter(Boolean)
    ),
  ];
};

export const getConnectableAuths = (connectString, services) => {
  if (Array.isArray(connectString)) {
    connectString = connectString.join(',');
  }
  return getUniqueConnectKeys(connectString)
    .map(connectKey => {
      const connectInfo = splitConnectKey(connectKey);

      const sourceService =
        connectInfo.sourceApi &&
        services.find(service => service.id === connectInfo.sourceApi);

      const destinationService =
        connectInfo.destinationApi &&
        ServiceUtils.getServiceByKeyWithOptionalVersion(
          services,
          connectInfo.destinationApi
        );

      // source_api:source_auth_identity:destination_api
      if (
        connectInfo.sourceApi &&
        connectInfo.sourceAuthIdentifier &&
        connectInfo.destinationApi
      ) {
        if (sourceService && destinationService) {
          return {
            connectKey,
            service: sourceService,
            destinationService,
            sourceAuthIdentifier: connectInfo.sourceAuthIdentifier,
          };
        }
        return null;
      }

      // source_api
      if (connectInfo.sourceApi) {
        if (sourceService) {
          return {
            connectKey,
            service: sourceService,
          };
        }
        return null;
      }

      // source_auth_id:destination_api
      if (connectInfo.sourceAuthId && connectInfo.destinationApi) {
        if (destinationService) {
          return {
            connectKey,
            sourceAuthId: connectInfo.sourceAuthId,
            destinationService,
          };
        }
      }

      return null;
    })
    .filter(Boolean);
};

const iconsHash = {
  private: 'lock',
  shared: 'team',
  restricted: 'restricted',
};

export const getIconForAuthType = authType => iconsHash[authType];

export const doesAuthHaveTargetApi = (api, auth = {}) =>
  (auth.selected_api === api || auth.api === api) &&
  !auth.destination_selected_api;

export const getAuthTrackingProps = ({ node, service, services, auths }) => {
  const selected_api = get(node, ['selected_api']);
  const nodeService = service || get(services, [selected_api], {});
  const doesAuthHaveNodeApi = partial(doesAuthHaveTargetApi, nodeService.key);
  const authArray = Array.isArray(auths) ? auths : values(auths);
  const authsForApi = authArray.filter(doesAuthHaveNodeApi);
  return {
    action: get(node, ['action']),
    auth_type: get(nodeService, ['authType']),
    node_id: get(node, ['id']),
    num_auths: authsForApi.length,
    selected_api,
    step_type: get(node, ['type_of']),
  };
};
